import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { type To, useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';

export const StyledAnchor = styled.a(({ theme }) => ({
  color: theme.palette.hyperlink,
  textDecoration: 'underline',
}));

export type LinkProps = {
  replace?: boolean;
  to: To;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

const isExternalUrl = (to: To): to is string => typeof to === 'string' && /^https?:\/\//i.test(to);

/**
 * Where an in-app destination points, as a path, so the anchor is a real link (focusable, and it opens in a
 * new tab) while a plain click still navigates in the app. A relative string has no location to resolve
 * against here, so it stays without an href.
 */
function resolveInternalHref(to: To): string | undefined {
  if (typeof to === 'string') return to.startsWith('/') ? to : undefined;

  if (!to.pathname?.startsWith('/')) return undefined;

  const search = to.search && !to.search.startsWith('?') ? `?${to.search}` : (to.search ?? '');
  const hash = to.hash && !to.hash.startsWith('#') ? `#${to.hash}` : (to.hash ?? '');

  return `${to.pathname}${search}${hash}`;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, to, onClick, children, replace, ...otherProps }, ref) => {
    const navigate = useNavigate();
    // An explicit href or an absolute URL leaves the app: the browser follows it, in a new tab.
    const externalHref = href || (isExternalUrl(to) ? to : undefined);
    const resolvedHref = externalHref ?? resolveInternalHref(to);

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Allow modifier clicks (cmd/ctrl/shift + click)
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.button !== 0) {
        return;
      }

      onClick?.(event);

      // Let a consumer's onClick cancel the navigation (e.g. it handles the click itself).
      if (event.defaultPrevented) {
        return;
      }

      if (!externalHref) {
        event.preventDefault();
        navigate(to, { replace });
      }
    };

    return (
      <a
        ref={ref}
        {...otherProps}
        href={resolvedHref}
        onClick={handleClick}
        {...(externalHref ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  }
);

export const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.hyperlink,
  cursor: 'pointer',
  textDecoration: 'underline',
}));
